// frontend/packages/business-console/src/hooks/useOfflineOutbox.js
//
// Work item 2.19 — shared offline-outbox hook. Design source:
// `docs/design/quotes-invoices-console.md` §7a.
//
// Contract:
//   const { items, enqueue, flush, isFlushing, lastError } = useOfflineOutbox({
//     kind: 'quote',                  // 'quote' | 'invoice' (and 'workflow-form' in Phase 5)
//     sync: async (item) => ({...}),  // POST to server; throws on failure;
//                                     // resolves to { serverId, serverNumber } on success
//   });
//
// Behaviour:
//   - On mount, subscribes to the outbox store and exposes the current
//     list of `pending` + `syncing` + `failed` items for the given kind.
//   - `enqueue({clientUuid, payload, route})` writes the item with
//     status `pending` and immediately tries to flush if online.
//   - `flush()` walks every `pending` item oldest-first and calls
//     `sync(item)`. Success → status `synced` + hydrated server ids.
//     Failure with a 4xx → status `failed` + lastError stamped on the
//     row. Failure with a 5xx / network error → status `pending`
//     reverted with a bumped `attemptCount`.
//   - `online` events trigger an automatic flush.
//   - Synced rows are pruned after 5 minutes (per §7a — keeps them
//     visible long enough for the operator to confirm).
//
// Phase 5 work item 5.2 reuses this hook for `WorkflowFormSubmission`
// — pass `kind: 'workflow-form'` and a `sync` that posts to
// `/api/v2/workflow-forms/...`. Do not re-implement the storage layer.

import { useCallback, useEffect, useRef, useState } from 'react';
import {
  listByStatus,
  put,
  pruneSynced,
  newClientUuid,
} from '../lib/outboxStore';
import useOnlineStatus from './useOnlineStatus';

const FLUSHABLE_STATUSES = ['pending', 'failed'];

function classifyError(err) {
  const status =
    err?.response?.status ||
    err?.status ||
    (err?.code === 'ERR_NETWORK' ? 0 : null);
  if (status && status >= 400 && status < 500) return 'fatal';
  return 'transient';
}

function errorMessage(err) {
  return (
    err?.response?.data?.error ||
    err?.response?.data?.message ||
    err?.message ||
    'Sync failed.'
  );
}

export default function useOfflineOutbox({ kind, sync }) {
  const online = useOnlineStatus();
  const [items, setItems] = useState([]);
  const [isFlushing, setIsFlushing] = useState(false);
  const [lastError, setLastError] = useState(null);
  const inFlightRef = useRef(false);
  const syncRef = useRef(sync);
  syncRef.current = sync;

  const refresh = useCallback(async () => {
    const list = await listByStatus({
      kind,
      status: ['pending', 'syncing', 'failed'],
    });
    list.sort((a, b) => (a.savedAt || 0) - (b.savedAt || 0));
    setItems(list);
  }, [kind]);

  const flush = useCallback(async () => {
    if (inFlightRef.current) return;
    if (!navigator || navigator.onLine === false) return;
    inFlightRef.current = true;
    setIsFlushing(true);
    setLastError(null);
    try {
      const pending = await listByStatus({
        kind,
        status: FLUSHABLE_STATUSES,
      });
      pending.sort((a, b) => (a.savedAt || 0) - (b.savedAt || 0));
      for (const item of pending) {
        await put({ ...item, status: 'syncing' });
        try {
          const result = await syncRef.current(item);
          await put({
            ...item,
            status: 'synced',
            serverId: result?.serverId || item.serverId || null,
            serverNumber: result?.serverNumber || item.serverNumber || null,
            attemptCount: (item.attemptCount || 0) + 1,
            lastError: null,
            savedAt: Date.now(),
          });
        } catch (err) {
          const kindOfError = classifyError(err);
          const message = errorMessage(err);
          await put({
            ...item,
            status: kindOfError === 'fatal' ? 'failed' : 'pending',
            attemptCount: (item.attemptCount || 0) + 1,
            lastError: message,
          });
          setLastError(message);
        }
      }
      await pruneSynced();
    } finally {
      inFlightRef.current = false;
      setIsFlushing(false);
      await refresh();
    }
  }, [kind, refresh]);

  const enqueue = useCallback(
    async ({ clientUuid, payload, route }) => {
      const id = clientUuid || newClientUuid();
      const now = Date.now();
      const existing = items.find((it) => it.clientUuid === id);
      await put({
        clientUuid: id,
        kind,
        route,
        payload,
        savedAt: now,
        status: 'pending',
        attemptCount: existing?.attemptCount || 0,
        lastError: null,
        serverId: existing?.serverId || null,
        serverNumber: existing?.serverNumber || null,
      });
      await refresh();
      // Fire-and-forget flush; surfaces via state.
      flush();
      return id;
    },
    [items, kind, refresh, flush]
  );

  useEffect(() => {
    refresh();
  }, [refresh]);

  useEffect(() => {
    if (online) flush();
  }, [online, flush]);

  return { items, enqueue, flush, isFlushing, lastError, online };
}
